// src/hooks/useMapboxSatellite.js
import { useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setSatelliteLoading } from '../redux/slices/Map/map'

const MAPBOX_SATELLITE_CONFIG = {
  sourceId: 'mapbox-satellite',
  layerId: 'mapbox-satellite-layer',
  tiles: [
    `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`,
  ],
  tileSize: 512,
  minzoom: 0,
  maxzoom: 22,
  attribution: '© Mapbox © OpenStreetMap',
}

export const useMapboxSatellite = (map, mapLoaded, options = {}) => {
  const { beforeLayer = 'clusters' } = options

  const dispatch = useDispatch()
  const satelliteEnabled = useSelector(state => state.mapState.satelliteEnabled)

  const initializedRef = useRef(false)
  const retryCountRef = useRef(0)
  const retryTimerRef = useRef(null)
  const idleHandlerRef = useRef(null)
  const tileLoadCleanupRef = useRef(null)
  const onInitializedRef = useRef(null)
  const MAX_RETRIES = 5

  const hasStyle = useCallback(() => {
    if (!map) return false
    try {
      return Boolean(map.getStyle?.())
    } catch {
      return false
    }
  }, [map])

  const isStyleReady = useCallback(() => {
    if (!map) return false
    try {
      return Boolean(map.getStyle?.()) && map.isStyleLoaded()
    } catch {
      return false
    }
  }, [map])

  const reconcileInitializedRef = useCallback(() => {
    if (!initializedRef.current) return
    if (!map || !hasStyle()) return
    if (!map.getLayer(MAPBOX_SATELLITE_CONFIG.layerId)) {
      initializedRef.current = false
    }
  }, [map, hasStyle])

  const enforceSatelliteOrder = useCallback(() => {
    if (!map || !isStyleReady()) return false
    const { layerId } = MAPBOX_SATELLITE_CONFIG
    if (!map.getLayer(layerId) || !map.getLayer(beforeLayer)) return false
    try {
      map.moveLayer(layerId, beforeLayer)
      return true
    } catch {
      return false
    }
  }, [map, beforeLayer, isStyleReady])

  // Cancela cualquier tracking de tiles activo.
  const cancelTileLoadTracking = useCallback(() => {
    if (tileLoadCleanupRef.current) {
      tileLoadCleanupRef.current()
      tileLoadCleanupRef.current = null
    }
  }, [])

  // Espera a que los tiles del viewport estén cargados para limpiar el loading.
  const trackTileLoad = useCallback(() => {
    cancelTileLoadTracking()

    const { sourceId } = MAPBOX_SATELLITE_CONFIG
    if (!map) {
      dispatch(setSatelliteLoading(false))
      return
    }

    try {
      if (map.isSourceLoaded(sourceId)) {
        dispatch(setSatelliteLoading(false))
        return
      }
    } catch {
      dispatch(setSatelliteLoading(false))
      return
    }

    let fallbackTimer = null

    const doCleanup = () => {
      map.off('sourcedata', handleSourceData)
      if (fallbackTimer) clearTimeout(fallbackTimer)
      tileLoadCleanupRef.current = null
    }

    const handleSourceData = e => {
      if (e.sourceId === sourceId && e.isSourceLoaded) {
        doCleanup()
        dispatch(setSatelliteLoading(false))
      }
    }

    // Fallback: si los tiles nunca terminan de cargar (red muy lenta), limpiar igual.
    fallbackTimer = setTimeout(() => {
      doCleanup()
      dispatch(setSatelliteLoading(false))
    }, 15000)

    tileLoadCleanupRef.current = doCleanup
    map.on('sourcedata', handleSourceData)
  }, [map, dispatch, cancelTileLoadTracking])

  const syncVisibility = useCallback(
    enabled => {
      if (!map || !hasStyle()) return
      const { layerId } = MAPBOX_SATELLITE_CONFIG
      if (!map.getLayer(layerId)) return
      try {
        map.setLayoutProperty(layerId, 'visibility', enabled ? 'visible' : 'none')
        if (enabled) enforceSatelliteOrder()
        else cancelTileLoadTracking()
      } catch (error) {
        console.error('❌ Error toggle satelital:', error)
        dispatch(setSatelliteLoading(false))
      }
    },
    [map, hasStyle, enforceSatelliteOrder, cancelTileLoadTracking, dispatch]
  )

  const scheduleOrderRepair = useCallback(() => {
    if (!map || idleHandlerRef.current) return
    const handler = () => {
      idleHandlerRef.current = null
      enforceSatelliteOrder()
    }
    idleHandlerRef.current = handler
    map.once('idle', handler)
  }, [map, enforceSatelliteOrder])

  // Solo configura source/layer. La visibilidad y el tile tracking
  // los maneja el caller a través de onInitializedRef.
  const initializeSatelliteLayer = useCallback(
    currentSatelliteEnabled => {
      if (!map) {
        dispatch(setSatelliteLoading(false))
        return false
      }

      if (!isStyleReady()) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
          retryTimerRef.current = setTimeout(() => {
            initializeSatelliteLayer(currentSatelliteEnabled)
          }, 250)
        } else {
          dispatch(setSatelliteLoading(false))
        }
        return false
      }

      const { sourceId, layerId, tiles, tileSize, minzoom, maxzoom, attribution } =
        MAPBOX_SATELLITE_CONFIG

      try {
        if (map.getSource(sourceId) && map.getLayer(layerId)) {
          initializedRef.current = true
          retryCountRef.current = 0
          enforceSatelliteOrder()
          onInitializedRef.current?.(currentSatelliteEnabled)
          return true
        }

        const referenceLayer = map.getLayer(beforeLayer)

        if (!referenceLayer && retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
          retryTimerRef.current = setTimeout(() => {
            initializeSatelliteLayer(currentSatelliteEnabled)
          }, 200)
          return false
        }

        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'raster',
            tiles,
            tileSize,
            minzoom,
            maxzoom,
            attribution,
          })
        }

        if (!map.getLayer(layerId)) {
          const layerConfig = {
            id: layerId,
            type: 'raster',
            slot: 'top',
            source: sourceId,
            minzoom: 0,
            maxzoom: 22,
            paint: {
              'raster-opacity': 1,
              'raster-fade-duration': 300,
              'raster-opacity-transition': { duration: 300, delay: 0 },
            },
            layout: { visibility: 'none' },
          }

          if (referenceLayer) {
            map.addLayer(layerConfig, beforeLayer)
          } else {
            map.addLayer(layerConfig)
            scheduleOrderRepair()
          }
        }

        initializedRef.current = true
        retryCountRef.current = 0
        onInitializedRef.current?.(currentSatelliteEnabled)
        return true
      } catch (error) {
        console.error('❌ Error inicializando satelital:', error)
        dispatch(setSatelliteLoading(false))
        return false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, beforeLayer, isStyleReady, enforceSatelliteOrder, scheduleOrderRepair, dispatch]
  )

  useEffect(() => {
    if (!map || !mapLoaded) return

    reconcileInitializedRef()

    if (!satelliteEnabled) {
      if (initializedRef.current) syncVisibility(false)
      return
    }

    // Callback centralizado: lo llama initializeSatelliteLayer al completar (sync o async)
    const handleInitialized = enabled => {
      syncVisibility(enabled)
      if (enabled) trackTileLoad()
      else dispatch(setSatelliteLoading(false))
    }

    if (initializedRef.current) {
      handleInitialized(true)
      return
    }

    onInitializedRef.current = handleInitialized
    retryCountRef.current = 0
    initializeSatelliteLayer(satelliteEnabled)

    const handleStyleData = () => {
      reconcileInitializedRef()
      if (!initializedRef.current) {
        retryCountRef.current = 0
        initializeSatelliteLayer(satelliteEnabled)
      }
    }

    map.on('styledata', handleStyleData)

    return () => {
      onInitializedRef.current = null
      map.off('styledata', handleStyleData)
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      if (idleHandlerRef.current) {
        map.off('idle', idleHandlerRef.current)
        idleHandlerRef.current = null
      }
    }
  }, [
    map,
    mapLoaded,
    satelliteEnabled,
    initializeSatelliteLayer,
    reconcileInitializedRef,
    syncVisibility,
    trackTileLoad,
    dispatch,
  ])
}

export default useMapboxSatellite
